import React, { useState, useRef, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { X, Check, CheckCheck } from 'lucide-react';

const REACTION_OPTIONS = [
  { emoji: '\u2764\uFE0F', key: 'heart' },
  { emoji: '\uD83D\uDC4D', key: 'thumbsup' },
  { emoji: '\uD83D\uDE02', key: 'laugh' },
  { emoji: '\uD83D\uDD25', key: 'fire' },
  { emoji: '\uD83D\uDE2E', key: 'wow' },
  { emoji: '\uD83D\uDE22', key: 'sad' },
];

const formatTime = (dateString) => {
  if (!dateString) return '';
  const d = new Date(dateString);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === yesterday.toDateString())
    return `Yesterday ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  return (
    d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  );
};

/* Lightbox overlay for full-size image viewing */
const ImageLightbox = ({ src, alt, onClose }) => {
  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
      onClick={onClose}
      data-testid="image-lightbox"
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-zinc-800/80 rounded-full hover:bg-zinc-700 transition-colors"
        data-testid="lightbox-close"
      >
        <X className="w-5 h-5 text-white" />
      </button>
      <img
        src={src}
        alt={alt || 'Full size image'}
        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

const ChatBubble = ({
  message,
  isMine,
  senderAvatar,
  senderName,
  compact = false,
  reactions = [],
  onReact,
  isLastOutgoing = false,
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const timeoutRef = useRef(null);

  const avatarLetter = (senderName || 'U')[0].toUpperCase();
  const hasImage = !!message.image_url;
  const hasText = !!message.content;

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setShowPicker(true);
  };
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setShowPicker(false), 400);
  };
  useEffect(
    () => () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const handleReact = (key) => {
    onReact?.(message.id, key);
    setShowPicker(false);
  };

  const activeReactions = reactions.filter((r) => r.count > 0);

  return (
    <>
      {lightboxSrc && (
        <ImageLightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />
      )}
      <div
        className={`flex ${isMine ? 'justify-end' : 'justify-start'} group relative ${compact ? 'mb-0.5' : 'mb-1'}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-testid={`chat-bubble-${message.id}`}
      >
        {/* Incoming avatar */}
        {!isMine && !compact && (
          <Avatar className="h-7 w-7 mr-2 mt-1 flex-shrink-0">
            {senderAvatar && <AvatarImage src={senderAvatar} alt={senderName} />}
            <AvatarFallback className="bg-zinc-700 text-[10px] text-zinc-300 font-bold">
              {avatarLetter}
            </AvatarFallback>
          </Avatar>
        )}
        {!isMine && compact && <div className="w-7 mr-2 flex-shrink-0" />}

        <div className="relative max-w-[70%]">
          {/* Reaction picker */}
          {showPicker && !message._sending && (
            <div
              className={`absolute ${isMine ? 'right-0' : 'left-0'} -top-8 flex items-center gap-0.5 bg-zinc-800 border border-zinc-700 rounded-full px-1.5 py-1 shadow-lg z-10`}
              data-testid="reaction-picker"
            >
              {REACTION_OPTIONS.map(({ emoji, key }) => {
                const mine = reactions.some((r) => r.reaction_type === key && r.mine);
                return (
                  <button
                    key={key}
                    onClick={() => handleReact(key)}
                    className={`w-7 h-7 flex items-center justify-center rounded-full hover:bg-zinc-700 transition-colors text-sm ${mine ? 'bg-zinc-600 ring-1 ring-orange-500/50' : ''}`}
                    data-testid={`reaction-btn-${key}`}
                  >
                    {emoji}
                  </button>
                );
              })}
            </div>
          )}

          {/* Bubble */}
          <div
            className={`rounded-2xl text-sm overflow-hidden ${
              isMine
                ? hasImage && !hasText
                  ? 'rounded-br-md'
                  : 'bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-br-md'
                : hasImage && !hasText
                  ? 'rounded-bl-md'
                  : 'bg-zinc-800 text-zinc-200 rounded-bl-md'
            } ${message._sending ? 'opacity-50' : ''}`}
            data-testid={`chat-bubble-content-${message.id}`}
          >
            {/* Image */}
            {hasImage && (
              <div
                className={`cursor-pointer ${hasText ? '' : 'rounded-2xl overflow-hidden'} ${
                  isMine
                    ? hasText ? 'bg-gradient-to-br from-orange-500 to-orange-600' : ''
                    : hasText ? 'bg-zinc-800' : ''
                }`}
                onClick={() => setLightboxSrc(message.image_url)}
                data-testid="chat-image"
              >
                <img
                  src={message.image_url}
                  alt="Chat attachment"
                  className={`max-w-full max-h-48 object-contain ${hasText ? 'rounded-t-2xl' : 'rounded-2xl'}`}
                  loading="lazy"
                />
              </div>
            )}

            {/* Text */}
            {hasText && (
              <div className={`px-3.5 py-2 ${
                !hasImage
                  ? ''
                  : isMine
                    ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white'
                    : 'bg-zinc-800 text-zinc-200'
              }`}>
                <p className="break-words whitespace-pre-wrap">{message.content}</p>
              </div>
            )}

            {/* Image-only: minimal bg for alignment */}
            {hasImage && !hasText && (
              <div className={`px-1 py-0.5 ${isMine ? '' : ''}`} />
            )}
          </div>

          {/* Reactions display */}
          {activeReactions.length > 0 && (
            <div className={`flex gap-0.5 mt-0.5 ${isMine ? 'justify-end' : 'justify-start'}`}>
              <div
                className="flex items-center gap-1 bg-zinc-800 border border-zinc-700 rounded-full px-1.5 py-0.5"
                data-testid="reaction-display"
              >
                {activeReactions.map((r) => {
                  const opt = REACTION_OPTIONS.find((o) => o.key === r.reaction_type);
                  return (
                    <button
                      key={r.reaction_type}
                      onClick={() => handleReact(r.reaction_type)}
                      className={`inline-flex items-center gap-0.5 text-xs rounded-full px-0.5 transition-colors hover:bg-zinc-700 ${r.mine ? 'text-white' : 'text-zinc-400'}`}
                      data-testid={`reaction-display-${r.reaction_type}`}
                    >
                      {opt?.emoji}
                      {r.count > 1 && <span className="text-[9px]">{r.count}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Timestamp */}
          <p
            className={`text-[9px] mt-0.5 px-1 ${isMine ? 'text-right text-zinc-500' : 'text-left text-zinc-600'}`}
          >
            {formatTime(message.created_at)}
          </p>

          {/* Read receipt — last outgoing message only */}
          {isMine && isLastOutgoing && !message._sending && (
            <div
              className="flex items-center justify-end gap-0.5 mt-0.5 px-1"
              data-testid="read-receipt"
            >
              {message.status === 'seen' ? (
                <>
                  <CheckCheck className="w-3 h-3 text-orange-400" />
                  <span className="text-[9px] text-orange-400">Seen</span>
                </>
              ) : message.status === 'delivered' ? (
                <>
                  <CheckCheck className="w-3 h-3 text-zinc-500" />
                  <span className="text-[9px] text-zinc-500">Delivered</span>
                </>
              ) : (
                <>
                  <Check className="w-3 h-3 text-zinc-600" />
                  <span className="text-[9px] text-zinc-600">Sent</span>
                </>
              )}
            </div>
          )}

          {/* Sending indicator */}
          {isMine && message._sending && (
            <div className="flex items-center justify-end gap-0.5 mt-0.5 px-1" data-testid="sending-indicator">
              <span className="text-[9px] text-zinc-600">Sending...</span>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export { ChatBubble, formatTime, REACTION_OPTIONS };
